import React, {useMemo, useState} from "react";
import {DataAPI, useData} from "../services/API";
import {ButtonLink, Link, Loading} from "./Components";
import GenerateReport from "./modals/GenerateReport";
import ReactQueryTable from "./ReactQueryTable";
import {Helmet} from "react-helmet";
import {OrdinalColumn} from "./ReactTable";
import AddEditDistribution from "./modals/AddEditDistribution";
import DeleteDistribution from "./modals/DeleteDistribution";
import RunDistributionNowButton from "./distributions/RunDistributionNowButton";
import {useReports} from "../hooks";
import {ReportNames} from "../Const";
import {formatDistributionParams, getLocationIdValue, getPrinterIdValue, parseDistributionParams, toPrinterOption} from "../commons/DistributionUtils";
import {describeQuartzCron} from "../commons/CronUtils";

const ReportIcons = {
    counters_day: "fa-calendar-day",
    counters_range: "fa-chart-line",
    consumables_range: "fa-exchange-alt",
};

const ReportDescriptions = {
    counters_day: "Stan liczników drukarek na wybrany dzień",
    counters_range: "Zmiany liczników w wybranym okresie",
    consumables_range: "Historia wymian materiałów eksploatacyjnych",
};

const Reports = () => {
    return <>
        <Helmet>
            <title>Raporty</title>
        </Helmet>
        {/* <PageHeader title="Raporty"/> */}
        <ReportList />
        <ReportDistribution />
    </>
}

const ReportList = () => {
    let reports = useReports();

    let noReports = !reports || reports.length === 0;

    if (noReports) {
        return <Loading msg="Pobieranie informacji o raportach"/>
    }
    return (
        <div className="reports-section">
            <h2>Generowanie</h2>
            <div className="report-cards">
            {
                reports.map((report) => {
                    const icon = ReportIcons[report.id] || "fa-file-alt";
                    const description = ReportDescriptions[report.id];
                    return (
                        <Link key={report.id} to={GenerateReport.url} state={{report}} withBackground={true} className="report-card">
                            <span className="report-card-icon"><i className={`fas ${icon}`}/></span>
                            <span className="report-card-body">
                                <span className="report-card-title">{report.name}</span>
                                {description && <span className="report-card-desc">{description}</span>}
                            </span>
                            <span className="report-card-arrow"><i className="fas fa-chevron-right"/></span>
                        </Link>
                    )
                })
            }
            </div>
        </div>
    )
}

const ReportDistribution = () => {
    const columns = useMemo(()=>{
        return [
            OrdinalColumn,
            { Header: 'Nazwa', accessor: 'name' },
            {
                Header: 'Rozkład',
                accessor: 'cron',
                Cell: ({row: {original}}) => <DistributionCronSummary cron={original.cron}/>
            },
            {
                Header: 'Raport',
                accessor: 'reportName',
                Cell: ({row: {original}}) => <span>{ReportNames[original.reportId]}</span>

            },
            {
                Header: 'Parametry',
                id: 'paramsSummary',
                disableSortBy: true,
                Cell: ({row: {original}}) => <DistributionParamsSummary distribution={original}/>
            },
            {
                Header: 'Akcje',
                id: 'actions',
                disableSortBy: true,
                Cell: ({row: {original}}) => <DistributionActions distribution={original}/>
            }
        ]
    }, []);

    return (
        <div className="reports-section">
            <div className="reports-section-header">
                <h2>Dystrybucja</h2>
                <ButtonLink variant="success" to={AddEditDistribution.buildUrl('new')} withBackground={true}>
                    <i className="fas fa-plus"/> Utwórz dystrybucję
                </ButtonLink>
            </div>
            <ReactQueryTable type="reportdistribution" columns={columns} queryFunction={DataAPI.getReportDistributionList}/>
        </div>
    )
}

const DistributionActions = ({distribution}) => {
    const [running, setRunning] = useState(false);

    return <div className="actions action-icons">
        <RunDistributionNowButton id={distribution.id} onRunningChange={setRunning} disabled={running} compact/>
        <ButtonLink size="sm" variant="link" to={AddEditDistribution.buildUrl(distribution.id)} withBackground={true} disabled={running} className="action-icon-btn action-edit">
            <i className="fas fa-pen"/>
            <span>Edytuj</span>
        </ButtonLink>
        <ButtonLink size="sm" variant="link" to={DeleteDistribution.buildUrl(distribution.id)} withBackground={true} disabled={running} className="action-icon-btn action-delete">
            <i className="fas fa-trash"/>
            <span>Usuń</span>
        </ButtonLink>
    </div>
}

const DistributionParamsSummary = ({distribution}) => {
    const params = useMemo(() => parseDistributionParams(distribution.params), [distribution.params]);
    const locationId = getLocationIdValue(params.locationId);
    const printerId = getPrinterIdValue(params.printerId);
    const { data: location } = useData(locationId, 'location', DataAPI.getLocation, {enabled: !!locationId});
    const { data: printer } = useData(printerId, 'printer', DataAPI.getPrinterDetails, {enabled: !!printerId});
    const summary = formatDistributionParams(distribution.reportId, params, {
        locationLabel: location ? location.path : null,
        printerLabel: printer ? toPrinterOption(printer).label : null,
    });
    return <span>{summary}</span>
}

const DistributionCronSummary = ({cron}) => {
    const summary = useMemo(() => describeQuartzCron(cron), [cron]);
    return <div className="distribution-cron-summary">
        <div>{summary}</div>
        <small className="text-muted">{cron || ''}</small>
    </div>
}


Reports.url = '/reports';

export default Reports;